import { Divider, HStack, Text, VStack } from "native-base";
import React from "react";

import ExternalImage from "@/components/atoms/ExternalImage";
import { OrderVoucherDetails } from "@/interfaces/domain/Order";
import { Store } from "@/interfaces/domain/Store";
import { VoucherDiscountTypesEnum } from "@/interfaces/domain/Voucher";
import { formatToCurrency } from "@/utils/helpers";

type CheckoutResumeProps = {
  store: Store;
  subtotal: number;
  total: number;
  itemsQuantity: number;
  shipmentValue: number;
  voucherDetails?: OrderVoucherDetails;
  serviceTaxValue?: number;
};

const CheckoutResume = ({
  store,
  itemsQuantity,
  serviceTaxValue,
  shipmentValue,
  subtotal,
  total,
  voucherDetails,
}: CheckoutResumeProps) => {
  const voucher = voucherDetails?.cupom;

  const isShipmentVoucher = [
    VoucherDiscountTypesEnum.SHIPMENT_DISCOUNT,
    VoucherDiscountTypesEnum.SHIPMENT_FREEE,
  ].includes(voucher?.tipo_desconto);
  const isDiscountVoucher = voucher?.tipo_desconto === VoucherDiscountTypesEnum.DISCOUNT;
  const isShipmentFreeVoucher = voucher?.tipo_desconto === VoucherDiscountTypesEnum.SHIPMENT_FREEE;
  const voucherInitialTotal = subtotal + shipmentValue;
  const voucherDiscount = isShipmentFreeVoucher
    ? voucherDetails?.frete_inicial
    : voucher?.valor_desconto;

  const getShipmentValueText = () => {
    const shipmentFreeLabel = "Grátis";
    if (isShipmentFreeVoucher) {
      return shipmentFreeLabel;
    }
    if (shipmentValue === 0) {
      return shipmentFreeLabel;
    }
    if (!shipmentValue) {
      return "-";
    }
    return formatToCurrency(shipmentValue);
  };

  return (
    <VStack my="2" p="2" bgColor="white.50" shadow="4" rounded="md">
      <HStack my="1" alignItems="center">
        <ExternalImage
          source={{ uri: store?.foto_estabelecimento }}
          width={52}
          height={52}
          contentFit="contain"
        />
        <Text flexGrow={1} flexBasis={1} mx="1" fontSize="lg" color="text.600">
          {store?.nome_fantasia}
        </Text>
      </HStack>
      <HStack py="1" justifyContent="space-between" alignItems="center">
        <Text bold>Subtotal</Text>
        <Text>{itemsQuantity} itens</Text>
        <Text color="primary.600" bold>
          {formatToCurrency(subtotal)}
        </Text>
      </HStack>
      <HStack py="1" justifyContent="space-between" alignItems="center">
        <Text bold>Frete</Text>
        <HStack space="2" justifyContent="space-between">
          {isShipmentVoucher && (
            <Text strikeThrough>{formatToCurrency(voucherDetails?.frete_inicial)}</Text>
          )}
          <Text color="primary.600" bold>
            {getShipmentValueText()}
          </Text>
        </HStack>
      </HStack>
      {!!voucher && (
        <HStack py="1" justifyContent="space-between" alignItems="center">
          <Text bold>Cupom de desconto</Text>
          <HStack space="2" justifyContent="space-between">
            <Text>{voucher.codigo}</Text>
            <Text>{formatToCurrency(voucherDiscount)}</Text>
          </HStack>
        </HStack>
      )}
      {!!serviceTaxValue && (
        <HStack py="1" justifyContent="space-between" alignItems="center">
          <Text bold>Taxa de serviço</Text>
          <Text color="primary.600" bold>
            {formatToCurrency(serviceTaxValue)}
          </Text>
        </HStack>
      )}
      <Divider />
      <HStack py="2" justifyContent="space-between" alignItems="center">
        <Text bold>Total</Text>
        <HStack space="2" justifyContent="space-between">
          {isDiscountVoucher && <Text strikeThrough>{formatToCurrency(voucherInitialTotal)}</Text>}
          <Text color="primary.600" bold>
            {formatToCurrency(total)}
          </Text>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default CheckoutResume;
